import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'

import { Dialog, UploadButton, snackbar } from 'components'
import CustomTextField from 'components/CustomTextField'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { StringUtils } from 'utils'

import { DrawerItem, UpdateDrawerItemApiData } from '../../types'
import { updateDrawerItem } from '../../api'
import { useStyles } from './styles'

const displayTitleRules = {
  displayTitle: [
    {
      validate: (t: string) => t.length <= 100,
      message: 'El título no puede tener más de 100 caracteres.',
    },
  ],
}

const nonNativeRules = {
  name: [
    {
      validate: (name: string) => !!name,
      message: 'Este campo es requerido.',
    },
  ],
  redirectUrl: [
    {
      validate: (url: string) => StringUtils.isURL(url),
      message: `Debe ser una URL válida.`,
    },
  ],
  ...displayTitleRules,
}

interface Props {
  handleClose: () => void
  onDone: (drawerItem: DrawerItem) => Promise<void>
  drawerItem: DrawerItem
}

const UpdateDrawerItemDialog: React.FC<Props> = ({ handleClose, onDone, drawerItem }) => {
  const classes = useStyles()
  const isNative = drawerItem.native

  const [name, setName] = useState(drawerItem.name)
  const [displayTitle, setDisplayTitle] = useState(drawerItem.displayTitle ?? '')
  const [redirectUrl, setRedirectUrl] = useState(drawerItem.redirectUrl ?? '')
  const [logo, setLogo] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState(drawerItem.logoUrl ?? '')

  const [updateDrawerItemApi, isLoading] = useApiCall<UpdateDrawerItemApiData, void>(
    updateDrawerItem,
  )

  const handleNameChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setName(e.target.value)
  }

  const handleDisplayTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setDisplayTitle(e.target.value)
  }

  const handleRedirectUrlChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setRedirectUrl(e.target.value)
  }

  const handleChangeLogo = (file: File) => {
    setLogo(file)
    setLogoPreview(URL.createObjectURL(file))
  }

  const fieldsNative = { displayTitle }
  const fieldsNonNative = { name, redirectUrl, logo, displayTitle }
  const { errors, hasErrors, isAnyFieldEmpty } = useCheckFormErrors(
    isNative ? fieldsNative : fieldsNonNative,
    isNative ? displayTitleRules : nonNativeRules,
    isNative ? undefined : ['name', 'redirectUrl'],
  )

  const onUpdateDrawerItem = async () => {
    try {
      if (isNative) {
        await updateDrawerItemApi({
          id: drawerItem.id,
          drawerItem: { displayTitle: displayTitle.trim() },
        })
        onDone({ ...drawerItem, displayTitle: displayTitle.trim() || null })
      } else {
        const newItem = {
          name,
          redirectUrl,
          logo: logo ?? undefined,
          displayTitle: displayTitle.trim(),
        }
        await updateDrawerItemApi({
          id: drawerItem.id,
          drawerItem: newItem,
        })
        onDone({ ...drawerItem, ...newItem, displayTitle: newItem.displayTitle || null })
      }
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo actualizar link. Intente de nuevo.')
    }
  }

  const isSubmitDisabled = isNative
    ? hasErrors || isLoading
    : hasErrors || isAnyFieldEmpty || isLoading

  return (
    <Dialog
      title={isNative ? 'Editar título del link' : 'Editar link'}
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Guardar"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onUpdateDrawerItem}
    >
      <Grid container spacing={3}>
        {isNative && (
          <Grid item xs={12} sm={12}>
            <TextField
              label="Clave interna (no editable)"
              value={drawerItem.name}
              fullWidth
              disabled
              variant="filled"
            />
          </Grid>
        )}
        <Grid item xs={12} sm={12}>
          <CustomTextField
            label="Título visible en la app"
            value={displayTitle}
            fullWidth
            onChange={handleDisplayTitleChange}
            error={errors.displayTitle?.hasError}
            helperText={
              errors.displayTitle?.message ||
              (isNative
                ? 'Opcional. Si queda vacío se muestra la clave interna.'
                : 'Opcional. Si queda vacío se muestra el nombre del link.')
            }
            disabled={isLoading}
            maxLength={100}
          />
        </Grid>
        {!isNative && (
          <>
            <Grid item xs={12} sm={12}>
              <CustomTextField
                label="Nombre"
                value={name}
                fullWidth
                onChange={handleNameChange}
                error={errors.name.hasError}
                helperText={errors.name.message}
                disabled={isLoading}
                required
                maxLength={100}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                inputProps={{ style: { textOverflow: 'ellipsis' } }}
                label="URL de redirección"
                fullWidth
                value={redirectUrl}
                onChange={handleRedirectUrlChange}
                error={errors.redirectUrl.hasError}
                helperText={errors.redirectUrl.message}
                disabled={isLoading}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <UploadButton
                id="DI-logo-upload-button"
                accept=".jpg, .jpeg, .png"
                label="Cambiar logo"
                onChange={handleChangeLogo}
              />
              <img src={logoPreview} alt="" className={classes.imagePreview} />
            </Grid>
          </>
        )}
      </Grid>
    </Dialog>
  )
}

export { UpdateDrawerItemDialog }
